// Vendor Libs
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { filter, map, orderBy, remove } from 'lodash';

// Components
import Button from '../components/core/button';

// Contexts
import { TabContext } from './tab-context';

// Styles
import './panel-context.scss';

let idCounter = 0;

export const PanelContext = React.createContext({
  getPanelTitle: undefined,
  hidePanel: undefined,
  isPanelMaximized: undefined,
  registerPanelAction: undefined,
  togglePanelMaximized: undefined,
  unregisterPanelAction: undefined,
  updatePanelAction: undefined,
});

export class PanelProvider extends Component {
  state = {
    panelActions: [],
  };

  // Notifies parent of panel action changes.
  onChange = () => {
    const { onChange } = this.props;
    const { panelActions } = this.state;
    const renderedIcons = {};
    const buttons = [];
    const hotkeys = {};

    map(orderBy(panelActions, 'weight'), (action) => {
      if (action.icon && !renderedIcons[action.icon]) {
        renderedIcons[action.icon] = true;
        buttons.push(this.renderPanelActionButton(action));
      }
      if (action.hotkey) {
        hotkeys[action.hotkey] = this.handleHotkey(action);
      }
    });

    // Call onChange if it's a function.
    if (typeof onChange === 'function') {
      onChange(this.getPanelId(), buttons, hotkeys);
    }
  };

  // Helper methods for retrieving IDs and titles
  getPanelId = () => {
    const { node } = this.props;
    return node.getId();
  };

  getPanelTitle = () => {
    const { node } = this.props;
    return node.getName();
  };

  getTabsetId = () => {
    const { node } = this.props;
    return node.getParent().getId();
  };

  // Action handlers
  handleActionClick = (icon, onClick, event) => {
    const { onClick: onClickProp, node } = this.props;
    const { panelActions } = this.state;

    if (icon) {
      map(panelActions, (panel) => {
        if (panel.icon === icon) {
          this.togglePanel(panel.onClick, event);
        }
      });
    } else {
      this.togglePanel(onClick, event);
    }
    if (typeof onClickProp === 'function') {
      onClickProp(node, this.getPanelId());
    }
  };

  handleHotkey = ({ groupByIcon, icon, onClick }) => (event) =>
    this.handleActionClick(groupByIcon ? icon : null, onClick, event);

  hidePanel = () => {
    const { deleteTab } = this.context;
    deleteTab(this.getPanelId(), true);
  };

  registerPanelAction = (panelAction) => {
    const registeredId = ++idCounter;
    this.setState(
      (prevState) => ({
        panelActions: [
          ...prevState.panelActions,
          { ...panelAction, registeredId },
        ],
      }),
      this.onChange
    );
    return registeredId;
  };

  updatePanelAction = (registeredId, panelAction) => {
    this.setState(
      (prevState) => ({
        panelActions: [
          ...filter(
            prevState.panelActions,
            (action) => action.registeredId !== registeredId
          ),
          { ...panelAction, registeredId },
        ],
      }),
      this.onChange
    );
  };

  unregisterPanelAction = (registeredId) => {
    if (registeredId) {
      this.setState(
        (prevState) => {
          const panelActions = [...prevState.panelActions];
          remove(panelActions, (action) => action.registeredId === registeredId);
          return { panelActions };
        },
        this.onChange
      );
    }
  };

  // Checks if the panel is maximized by delegating to TabContext.
  isPanelMaximized = () => {
    const { isTabMaximized } = this.context;
    return isTabMaximized(this.getTabsetId());
  };

  // Toggles panel maximization by delegating to TabContext.
  togglePanelMaximized = () => {
    const { toggleTabMaximized } = this.context;
    toggleTabMaximized(this.getTabsetId());
  };

  // Executes an onClick callback for panel actions.
  togglePanel = (onClick, event) => {
    if (onClick) {
      onClick(event);
    }
  };

  // Renders a button for a panel action.
  renderPanelActionButton = ({ hotkey, icon, onClick, registeredId, title }) => {
    const props = {
      className: classnames(
        'panel-provider--action-toggle',
        icon ? `panel-provider--action-toggle_${icon}` : null
      ),
      icon,
      onClick: (event) => this.handleActionClick(icon, onClick, event),
      title: hotkey ? `${title} (${hotkey})` : title,
    };

    return <Button key={registeredId} {...props} />;
  };

  render() {
    const { children } = this.props;
    return (
      <PanelContext.Provider
          value={{
            getPanelTitle: this.getPanelTitle,
            hidePanel: this.hidePanel,
            isPanelMaximized: this.isPanelMaximized,
            registerPanelAction: this.registerPanelAction,
            togglePanelMaximized: this.togglePanelMaximized,
            unregisterPanelAction: this.unregisterPanelAction,
            updatePanelAction: this.updatePanelAction,
          }}
      >
        {children}
      </PanelContext.Provider>
    );
  }
}

PanelProvider.propTypes = {
  children: PropTypes.node,
  node: PropTypes.object.isRequired,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
};

PanelProvider.defaultProps = {
  onChange: () => {},
  onClick: () => {},
};

PanelProvider.contextType = TabContext;
